import * as React from 'react'
import { useNavigate, useLocation, useParams } from 'react-router-dom'
import { useVehicles } from '@/modules/shared/context/vehicles/VehiclesContext'
import { FaIcon, faTruckSolid, faClose } from '@/lib/fa'
import { LoaderIcon } from '@/app/features/icons/Loader'
import clx from 'classnames'
import SearchColor from '../../../assets/icons_new/SearchColor.svg'
import SearchInput from '../../drivers/shared/SearchInput'
import VehicleGroupPicker from '../shared/VehicleGroupPicker'
import { CompanyNameBadge } from '@/modules/drivers/shared/viewBars/CompanyNameBadge'

import { VehicleGroupModel } from '@/app/models/vehicle/vehicleGroupModel'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import Checkbox from '../../drivers/shared/Checkbox'
import { useSideVehicleSelection } from '../context/SideVehicleSelectionContext'
import { pathForSelectedVehicle } from '../utils/resolveVehiclePath'

type Props = {
  companyId: string
}

const SideVehiclesTable: React.FC<Props> = ({ companyId }) => {
  const {
    vehicles,
    searchResults,
    search,
    setSearch,
    selectedVehicleId,
    setSelectedVehicleId,
    isLoadingVehicles,
    groups,
    setActualGroup,
    actualGroup,
    refresh,
    hasMore,
    loadMore,
    isLoadingMore,
    fetchAllVehicleIds,
  } = useVehicles()

  const { sideCheckedIds, toggleSide, toggleAllSide, clearSide } =
    useSideVehicleSelection()

  const { t } = useLocalize()
  const [showSearch, setShowSearch] = React.useState(false)
  const [isSelectingAll, setIsSelectingAll] = React.useState(false)

  const handleToggleAll = React.useCallback(
    async (checked: boolean) => {
      if (!checked) {
        clearSide()
        return
      }
      setIsSelectingAll(true)
      try {
        const allIds = await fetchAllVehicleIds()
        toggleAllSide(allIds, true)
      } catch (error) {
        console.error('Error selecting all vehicles:', error)
        toggleAllSide(
          vehicles.map((v) => v.id),
          true,
        )
      } finally {
        setIsSelectingAll(false)
      }
    },
    [fetchAllVehicleIds, toggleAllSide, clearSide, vehicles],
  )
  const containerRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    if (!showSearch) return
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setShowSearch(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showSearch])

  const handleSelectGroup = (option: VehicleGroupModel | null) => {
    if (!option || option.id === 0) {
      setActualGroup(null)
    } else {
      setActualGroup(option)
    }
  }

  const navigate = useNavigate()
  const location = useLocation()
  const { vehicleId: vehicleIdParam } = useParams<{ vehicleId?: string }>()
  const activeId = vehicleIdParam ?? ''

  const handleSelectVehicle = (vehicleId: string) => {
    setSelectedVehicleId(vehicleId)

    const { pathname, search: qs, hash } = location
    navigate({
      pathname: pathForSelectedVehicle(pathname, companyId, vehicleId),
      search: qs,
      hash,
    })
  }

  const highlightMatch = (text: string, query: string) => {
    if (!query) return text
    const regex = new RegExp(`(${query})`, 'gi')
    const parts = text.split(regex)
    return (
      <>
        {parts.map((part, i) =>
          regex.test(part) ? (
            <span key={i} className="tw-bg-[#FFF47B]">
              {part}
            </span>
          ) : (
            part
          ),
        )}
      </>
    )
  }

  const listRef = React.useRef<HTMLDivElement>(null)
  React.useEffect(() => {
    const el = listRef.current
    if (!el) return
    const handleScroll = () => {
      if (
        el.scrollHeight - el.scrollTop - el.clientHeight < 100 &&
        hasMore &&
        !isLoadingMore
      ) {
        loadMore()
      }
    }
    el.addEventListener('scroll', handleScroll)
    return () => el.removeEventListener('scroll', handleScroll)
  }, [hasMore, isLoadingMore, loadMore])

  return (
    <div className="tw-flex tw-h-full tw-w-[300px] tw-max-w-[300px] tw-flex-col">
      <div className="tw-p-2 tw-pt-3.5">
        <CompanyNameBadge fullWidth />
      </div>
      <div
        ref={containerRef}
        className="tw-z-[100] tw-flex tw-flex-shrink-0 tw-items-center tw-gap-2 tw-bg-white tw-pr-4 tw-shadow-sm"
      >
        <div className="tw-flex tw-w-fit tw-items-center tw-gap-2 tw-p-3">
          <div
            className={`tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-border-2 tw-p-1 ${showSearch ? 'tw-rounded-lg tw-border-primary' : 'tw-border-transparent'}`}
            onClick={() => setShowSearch((prev) => !prev)}
          >
            <img src={SearchColor} alt="Search Icon" className="tw-w-6" />
          </div>
          <div className="tw-top-30 tw-absolute">
            {showSearch && (
              <SearchInput
                search={search}
                searchResults={vehicles.length}
                setSearch={setSearch}
              />
            )}
          </div>
        </div>
        <div className="tw-z-[200] tw-w-full">
          <VehicleGroupPicker
            fullWidth
            companyId={companyId}
            refreshGroups={refresh}
            name={t('common.all')}
            options={groups}
            onSelect={handleSelectGroup}
            actualGroup={actualGroup}
          />
        </div>
      </div>

      <div
        ref={listRef}
        className="custom-scrollbar tw-flex-1 tw-overflow-y-auto"
      >
        {isLoadingVehicles ? (
          <div className="tw-flex tw-h-full tw-items-center tw-justify-center">
            <LoaderIcon className="tw-animate-spin" />
          </div>
        ) : vehicles.length === 0 ? (
          <div className="tw-p-4 tw-text-center tw-text-xs tw-text-content-base">
            Brak pojazdów
          </div>
        ) : (
          <ul className="tw-flex tw-flex-col">
            <li className="tw-flex tw-items-center tw-gap-2 tw-border-b tw-border-base-200 tw-bg-content-primary tw-px-3 tw-py-2">
              <Checkbox
                checked={
                  vehicles.length > 0 &&
                  vehicles.every((v) => sideCheckedIds.includes(v.id))
                }
                onChange={(checked) => {
                  void handleToggleAll(checked)
                }}
              />
              <span className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-content-base">
                {isSelectingAll && (
                  <LoaderIcon className="tw-h-3 tw-w-3 tw-animate-spin" />
                )}
                {sideCheckedIds.length} / {searchResults}
              </span>

              <button
                type="button"
                disabled={sideCheckedIds.length === 0}
                onClick={clearSide}
                title="Wyczyść zaznaczone pojazdy"
                className={`tw-ml-auto tw-flex tw-flex-shrink-0 tw-items-center tw-gap-1 tw-rounded-md tw-px-2 tw-py-0.5 tw-text-xs tw-text-content-base tw-transition-colors hover:tw-bg-base-200 ${sideCheckedIds.length === 0 ? 'tw-cursor-not-allowed tw-opacity-50' : 'tw-cursor-pointer'}`}
              >
                <FaIcon icon={faClose} className="tw-h-3 tw-w-3" />
                <span>Wyczyść</span>
              </button>
            </li>
            {vehicles.map((vehicle) => (
              <li
                key={vehicle.id}
                onClick={() => handleSelectVehicle(vehicle.id)}
                className={clx(
                  'tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-border-b tw-border-base-300/50 tw-px-3 tw-py-2 tw-text-sm tw-transition-colors hover:tw-bg-primary/10',
                  activeId === vehicle.id && 'tw-bg-primary/20 tw-font-medium',
                  !vehicle.isActive && 'tw-opacity-50',
                )}
              >
                <div
                  className="tw--m-2 tw-grid tw-place-items-center tw-p-2"
                  onClick={(e) => {
                    e.stopPropagation()
                    toggleSide(vehicle.id)
                  }}
                >
                  <Checkbox
                    checked={sideCheckedIds.includes(vehicle.id)}
                    onChange={() => toggleSide(vehicle.id)}
                  />
                </div>
                <FaIcon
                  icon={faTruckSolid}
                  className="tw-h-3.5 tw-w-3.5 tw-flex-shrink-0 tw-text-content-base"
                />
                <span className="tw-min-w-0 tw-truncate tw-text-content-base">
                  {highlightMatch(vehicle.registrationNumber ?? '', search)}
                </span>
              </li>
            ))}
            {isLoadingMore && (
              <li className="tw-flex tw-justify-center tw-py-2">
                <LoaderIcon className="tw-h-4 tw-w-4 tw-animate-spin" />
              </li>
            )}
          </ul>
        )}
      </div>
    </div>
  )
}

export default SideVehiclesTable
